import {observer} from 'mobx-react';
import React, {useCallback, useState} from 'react';

import {ShoppingListImpressionEvent} from '@udemy/cart';
import {Tracker, TrackImpression} from '@udemy/event-tracking';
import {useFunnelLogContextStore} from '@udemy/funnel-tracking';
import {noop} from '@udemy/shared-utils';
import {
    SHOPPING_LIST_ACTIONS,
    ShoppingClient,
    shoppingItemTypes as itemTypes,
    ShoppingList as UdemyShoppingList,
    ShoppingListActionsValues,
    ShoppingListName,
    ShoppingItem as UdemyShoppingItem,
} from '@udemy/shopping';

import {ItemsOfSameBuyableType} from '../shopping-item/items-of-same-buyable-type.react-component';
import {ActionSupportedLayoutsProps, CartItem} from '../shopping-item/types';
import {EmptyCartShoppingList} from './empty-shopping-list.react-component';

export interface ShoppingListTrackActionsProps {
    action: ShoppingListActionsValues;
    item: UdemyShoppingItem;
    destListName?: string;
}

interface ShoppingListProps {
    listName: ShoppingListName;
    trackingContext?: string;
    performanceMarkName?: string;
    trackAction?: ({action, item, destListName}: ShoppingListTrackActionsProps) => void;
    actionsLayout: ActionSupportedLayoutsProps;
    filterByBuyableType?: string;
}

export const ShoppingList: React.FC<ShoppingListProps> = observer((props) => {
    const {listName, trackAction = noop, filterByBuyableType} = props;
    const shoppingClient = ShoppingClient;
    const [list] = useState<UdemyShoppingList>(shoppingClient.lists[listName]);
    const funnelLogContextStore = useFunnelLogContextStore();

    const [statusMessage, setStatusMessage] = useState('');

    const trackImpression = () => {
        Tracker.publishEvent(new ShoppingListImpressionEvent('udlite', listName));
    };

    const usesLatestDiscount = useCallback(
        (item: CartItem) => {
            if (
                item.current_discount?.campaign?.show_code &&
                item.current_discount.code !== undefined
            ) {
                const currentCode = item.current_discount.code;
                return (
                    shoppingClient.discounts.codes &&
                    shoppingClient.discounts.codes.indexOf(currentCode) === 0
                );
            }
            return false;
        },
        [shoppingClient.discounts.codes],
    );

    const add = useCallback(
        (item: CartItem) => {
            return () => {
                return shoppingClient.addToList(listName, [item.buyable]).then(() => {
                    trackAction({action: SHOPPING_LIST_ACTIONS.ADD, item});
                    setStatusMessage('Item added to cart.' + item.buyable.title);
                });
            };
        },
        [listName, shoppingClient, trackAction, setStatusMessage],
    );

    const remove = useCallback(
        (item: CartItem) => {
            return () => {
                return shoppingClient.removeFromList(listName, item).then(() => {
                    trackAction({action: SHOPPING_LIST_ACTIONS.REMOVE, item});
                    setStatusMessage('Item removed from cart.' + item.buyable.title);
                });
            };
        },
        [listName, shoppingClient, trackAction, setStatusMessage],
    );

    const moveToList = useCallback(
        (destName: ShoppingListName, item: CartItem) => {
            return () => {
                return shoppingClient.moveToList(listName, destName, item).then(() => {
                    trackAction({
                        action: SHOPPING_LIST_ACTIONS.MOVE,
                        item,
                        destListName: destName,
                    });
                    if (destName === 'saved_for_later' || destName === 'cart') {
                        setStatusMessage('Item  moved.' + item.buyable.title);
                    }
                });
            };
        },
        [listName, shoppingClient, trackAction, setStatusMessage],
    );

    const allProps = useCallback(() => {
        const actions = {
            add,
            remove,
            moveToList,
        };

        return {
            actions,
            usesLatestDiscount,
            funnelLogContextStore,
            ...props,
        };
    }, [add, funnelLogContextStore, moveToList, props, remove, usesLatestDiscount]);

    const displayItems = filterByBuyableType
        ? list.items.filter((item) => item.buyable.buyable_object_type === filterByBuyableType)
        : list.items;

    let content;
    if (list.isEmpty) {
        content = (
            <div>
                <EmptyCartShoppingList />
                <div aria-live="polite" role="status" className="ud-sr-only" aria-atomic="true">
                    <p>Your cart is empty</p>
                </div>
            </div>
        );
    } else {
        content = (
            <TrackImpression trackFunc={trackImpression}>
                <div>
                    {itemTypes.map((itemType, ind) => (
                        <ItemsOfSameBuyableType
                            key={ind}
                            items={displayItems}
                            itemType={itemType}
                            {...allProps()}
                        />
                    ))}
                </div>
            </TrackImpression>
        );
    }
    return (
        <div>
            {content}
            <div aria-live="polite" role="status" className="ud-sr-only" aria-atomic="true">
                <p>{statusMessage}</p>
            </div>
        </div>
    );
});
